/**
 * src/lib/hooks/useProjectData.js — Project & Bundle domain hook.
 *
 * Owns: projects, bundles, stageFlows, stageFlowSteps state,
 *       all project/bundle mutation callbacks.
 *
 * Extracted from useAppData.js (Phase 81-01).
 */

import { useState, useCallback } from 'react';
import { fetchProjects, fetchBundles, fetchStageFlows, fetchStageFlowSteps, createProject, moveProjectStage, updateProject, updateBundle, createBundle } from '../api/projects';
import { deleteBundle } from '../api/bundles';
import { logAction, logResult, logMergeDecision, logEvent } from '../observability';
import { logAppError } from '../errorReporter';
import { mergeWithOptimistic } from '../mergeWithOptimistic';
import { reconcileCollection } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';
import { maybeEnqueueOffline, newOfflineId } from '../offline/enqueueOffline';

export function useProjectData({ scheduleReload, stages, projectTypes, showError, offlineEnabled = false }) {
  const [projects, setProjects] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [stageFlows, setStageFlows] = useState([]);
  const [stageFlowSteps, setStageFlowSteps] = useState([]);
  // Safe no-op fallback so existing callers/tests that don't pass showError
  // still get correct behaviour (just no toast).
  const notifyError = showError || (() => {});

  const handleCreateProject = useCallback(async (payload) => {
    const cid = logAction('projects:createProject', { project_name: payload.project_name });
    const tempId = '__pending_proj_' + Date.now();
    const optimistic = {
      id: tempId,
      project_name: payload.project_name || '',
      short_description: payload.client_name || '',
      stage_id: null,
      stages: null,
      user_profiles: null,
      vessels: null,
      project_types: null,
      _pending: true,
    };
    setProjects(prev => [optimistic, ...prev]);

    if (offlineEnabled && !getOnline()) {
      const offlineId = newOfflineId();
      const realRow = { ...optimistic, id: offlineId, _pendingSync: true };
      setProjects(prev => prev.map(p => p.id === tempId ? realRow : p));
      await maybeEnqueueOffline({ offlineEnabled, domain: 'projects', type: 'create', entity_id: offlineId, payload: { ...payload, id: offlineId } });
      logResult('projects:createProject', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: realRow };
    }

    try {
      const result = await createProject(payload);
      if (result?.success) {
        if (result.record) {
          setProjects(prev => prev.map(p => p.id === tempId ? result.record : p));
        }
        logResult('projects:createProject', 'success', { correlationId: cid, project_id: result?.record?.id });
        scheduleReload(500, 'createProject');
      } else {
        // Bug 1 + Bug 2 fix (2026-04-24): emit a structured app_error_logs row
        // per failure so the CEO's "stuck in creating" symptom is diagnosable
        // from SQL, not just console output. CLAUDE.md rule: "every mutation
        // logs action_intent + action_result". See docs/CLAUDE.md.
        setProjects(prev => prev.filter(p => p.id !== tempId));
        logResult('projects:createProject', 'rollback', { correlationId: cid, error: result?.error, code: result?.code });
        logEvent('project_create_failed', result?.error || 'rollback', {
          correlationId: cid,
          code: result?.code || 'UNKNOWN',
          project_name: payload.project_name,
        });
      }
      return result;
    } catch (e) {
      setProjects(prev => prev.filter(p => p.id !== tempId));
      logAppError({
        type: 'project_create_exception',
        message: e.message,
        stack: e.stack,
        metadata: { correlationId: cid, project_name: payload.project_name },
      });
      logResult('projects:createProject', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message, code: 'EXCEPTION' };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleMoveProjectStage = useCallback(async (recordId, stage) => {
    const cid = logAction('projects:moveStage', { recordId, stage });
    const targetStage = stages.find(
      s => s.id === stage
        || (s.label_en || '').toLowerCase() === (stage || '').toLowerCase()
        || (s.stage_name || '').toLowerCase() === (stage || '').toLowerCase()
    );

    let prevProject = null;
    setProjects(prev => {
      prevProject = prev.find(p => p.id === recordId) || null;
      return prev.map(p =>
        p.id === recordId
          ? { ...p, stage_id: targetStage?.id || stage, stages: targetStage || p.stages }
          : p
      );
    });

    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'projects', type: 'move_stage', entity_id: recordId, payload: { stage } });
      if (q.queued) {
        setProjects(prev => prev.map(p => p.id === recordId ? { ...p, _pendingSync: true } : p));
        logResult('projects:moveStage', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await moveProjectStage(recordId, stage);
      if (result?.success) {
        logResult('projects:moveStage', 'success', { correlationId: cid });
        scheduleReload(500, 'moveProjectStage');
      } else if (prevProject) {
        setProjects(prev => prev.map(p => p.id === recordId ? prevProject : p));
        logResult('projects:moveStage', 'rollback', { correlationId: cid, error: result?.error });
        notifyError(`Couldn't move "${prevProject.project_name || 'project'}" — ${result?.error || 'unknown error'}`);
      }
      return result;
    } catch (e) {
      if (prevProject) {
        setProjects(prev => prev.map(p => p.id === recordId ? prevProject : p));
      }
      logAppError(e, 'useProjectData:moveProjectStage');
      logResult('projects:moveStage', 'error', { correlationId: cid, error: e.message });
      notifyError(`Couldn't move project: ${e.message}`);
      return { success: false, error: e.message };
    }
  }, [scheduleReload, stages, notifyError, offlineEnabled]);

  const handleUpdateProject = useCallback(async (projectId, updates) => {
    const cid = logAction('projects:updateProject', { projectId, fields: Object.keys(updates) });
    let prevProject = null;
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      prevProject = p;
      const next = { ...p };
      if (updates.project_type_id !== undefined) {
        next.project_type_id = updates.project_type_id || null;
        const pt = projectTypes.find(t => t.id === updates.project_type_id);
        if (pt) next.project_types = pt;
      }
      if (updates.project_name !== undefined) next.project_name = updates.project_name;
      if (updates.vessel_id !== undefined) next.vessel_id = updates.vessel_id || null;
      if (updates.stage_id !== undefined) next.stage_id = updates.stage_id || null;
      if (updates.owner_id !== undefined) next.owner_id = updates.owner_id || null;
      if (updates.parent_project_id !== undefined) next.parent_project_id = updates.parent_project_id || null;
      if (updates.total_budget_hours !== undefined) next.total_budget_hours = updates.total_budget_hours;
      return next;
    }));
    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'projects', type: 'update', entity_id: projectId, payload: updates });
      if (q.queued) {
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, _pendingSync: true } : p));
        logResult('projects:updateProject', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateProject(projectId, updates);
      if (result?.success) {
        logResult('projects:updateProject', 'success', { correlationId: cid });
        scheduleReload(500, 'updateProject');
      } else if (prevProject) {
        setProjects(prev => prev.map(p => p.id === projectId ? prevProject : p));
        logResult('projects:updateProject', 'rollback', { correlationId: cid, error: result?.error });
        notifyError(`Couldn't save project changes — ${result?.error || 'unknown error'}`);
      }
      return result;
    } catch (e) {
      if (prevProject) {
        setProjects(prev => prev.map(p => p.id === projectId ? prevProject : p));
      }
      logAppError(e, 'useProjectData:updateProject');
      logResult('projects:updateProject', 'error', { correlationId: cid, error: e.message });
      notifyError(`Couldn't save project changes: ${e.message}`);
      return { success: false, error: e.message };
    }
  }, [scheduleReload, projectTypes, notifyError, offlineEnabled]);

  const handleUpdateBundle = useCallback(async (bundleId, updates) => {
    const cid = logAction('projects:updateBundle', { bundleId });
    const cleanUpdates = {};
    for (const [k, v] of Object.entries(updates)) {
      if (!k.startsWith('_')) cleanUpdates[k] = v;
    }
    let prevBundle = null;
    setBundles(prev => prev.map(b => {
      if (b.id !== bundleId) return b;
      prevBundle = b;
      return { ...b, ...cleanUpdates };
    }));
    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'projects', type: 'update_bundle', entity_id: bundleId, payload: updates });
      if (q.queued) {
        setBundles(prev => prev.map(b => b.id === bundleId ? { ...b, _pendingSync: true } : b));
        logResult('projects:updateBundle', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateBundle(bundleId, updates);
      if (result?.success) {
        logResult('projects:updateBundle', 'success', { correlationId: cid });
        scheduleReload(500, 'updateBundle');
      } else if (prevBundle) {
        setBundles(prev => prev.map(b => b.id === bundleId ? prevBundle : b));
        logResult('projects:updateBundle', 'rollback', { correlationId: cid, error: result?.error });
        notifyError(`Couldn't save bundle "${prevBundle.title || 'bundle'}" — ${result?.error || 'unknown error'}`);
      }
      return result;
    } catch (e) {
      if (prevBundle) {
        setBundles(prev => prev.map(b => b.id === bundleId ? prevBundle : b));
      }
      logAppError(e, 'useProjectData:updateBundle');
      logResult('projects:updateBundle', 'error', { correlationId: cid, error: e.message });
      notifyError(`Couldn't save bundle: ${e.message}`);
      return { success: false, error: e.message };
    }
  }, [scheduleReload, notifyError]);

  const handleCreateBundle = useCallback(async (payload) => {
    const cid = logAction('projects:createBundle', { title: payload.title });
    try {
      const result = await createBundle(payload);
      if (result?.success) {
        logResult('projects:createBundle', 'success', { correlationId: cid });
        scheduleReload(500, 'createBundle');
      } else {
        logResult('projects:createBundle', 'rollback', { correlationId: cid, error: result?.error });
        notifyError(`Couldn't save bundle "${payload.title || 'bundle'}" — ${result?.error || 'unknown error'}`);
      }
      return result;
    } catch (e) {
      logAppError(e, 'useProjectData:createBundle');
      logResult('projects:createBundle', 'error', { correlationId: cid, error: e.message });
      notifyError(`Couldn't save bundle: ${e.message}`);
      return { success: false, error: e.message };
    }
  }, [scheduleReload, notifyError]);

  const handleDeleteBundle = useCallback(async (bundleId) => {
    const cid = logAction('projects:deleteBundle', { bundleId });
    try {
      const result = await deleteBundle(bundleId);
      if (result?.success) {
        logResult('projects:deleteBundle', 'success', { correlationId: cid });
        scheduleReload(500, 'deleteBundle');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useProjectData:deleteBundle');
      logResult('projects:deleteBundle', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // ─── Fetch / Apply / Merge (Phase 81-02) ───────────────────
  const fetchAll = useCallback(async () => {
    const [projects, bundles, stageFlows, stageFlowSteps] = await Promise.all([
      fetchProjects().catch(e => { console.warn('[ProjectData] Projects failed:', e.message); return []; }),
      fetchBundles().catch(() => []),
      fetchStageFlows().catch(() => []),
      fetchStageFlowSteps().catch(() => []),
    ]);
    if (offlineEnabled) {
      const online = getOnline();
      const [p, b, sf, sfs] = await Promise.all([
        reconcileCollection('projects', projects, online),
        reconcileCollection('bundles', bundles, online),
        reconcileCollection('stageFlows', stageFlows, online),
        reconcileCollection('stageFlowSteps', stageFlowSteps, online),
      ]);
      return { projects: p, bundles: b, stageFlows: sf, stageFlowSteps: sfs };
    }
    return { projects, bundles, stageFlows, stageFlowSteps };
  }, [offlineEnabled]);

  const applyFetchResult = useCallback((data) => {
    setProjects(Array.isArray(data.projects) ? data.projects : []);
    setBundles(Array.isArray(data.bundles) ? data.bundles : []);
    setStageFlows(Array.isArray(data.stageFlows) ? data.stageFlows : []);
    setStageFlowSteps(Array.isArray(data.stageFlowSteps) ? data.stageFlowSteps : []);
  }, []);

  const mergeAll = useCallback((data, optimisticTimestamps, now) => {
    const merge = (setter, newData) => {
      if (!newData) return;
      setter(prev => {
        const { merged, anyChanged, expiredIds } = mergeWithOptimistic(prev, newData, optimisticTimestamps, now);
        for (const { id, age_ms } of expiredIds) logMergeDecision(id, 'ttl_expired', { age_ms });
        return anyChanged ? merged : prev;
      });
    };
    merge(setProjects, data.projects);
    merge(setBundles, data.bundles);
    merge(setStageFlows, data.stageFlows);
    merge(setStageFlowSteps, data.stageFlowSteps);
  }, []);

  return {
    projects, setProjects,
    bundles, setBundles,
    stageFlows, setStageFlows,
    stageFlowSteps, setStageFlowSteps,
    handleCreateProject,
    handleMoveProjectStage,
    handleUpdateProject,
    handleUpdateBundle,
    handleCreateBundle,
    handleDeleteBundle,
    fetchAll,
    applyFetchResult,
    mergeAll,
  };
}
